import {Observable, Observer} from "rxjs";

export function stringCompare<T>(from: T, to: T, propertyGet: (obj: T) => string): number {
  return (propertyGet(from) || '').localeCompare((propertyGet(to) || ''));
}

export function numberCompare<T>(from: T, to: T, propertyGet: (obj: T) => number): number {
  const numberFrom = propertyGet(from);
  const numberTo = propertyGet(to);
  if (numberFrom < numberTo) {
    return -1;
  }
  if (numberFrom > numberTo) {
    return 1;
  }
  return 0;
}

export function isNullOrWhiteSpace(str: string) {
  return str === null || str === undefined || str.match(/^ *$/) !== null;
}

export function isValue(val: string | number) {
  if (val === null) {
    return false;
  }
  if (val === undefined) {
    return false;
  }
  if (!isNaN(Number(val)) && Number.isFinite(Number(val))) {
    return false;
  }
  return true;
}

export function stringifyJson(o: any): string {
  const cache = [];
  return JSON.stringify(o, function (key, value) {
    if (typeof value === 'object' && value !== null) {
      if (cache.indexOf(value) !== -1) {
        // Duplicate reference found
        try {
          // If this value does not reference a parent it can be deduped
          return JSON.parse(JSON.stringify(value));

        } catch (error) {
          // discard key if value cannot be deduped
          return;
        }
      }
      // Store value in our collection
      cache.push(value);
    }
    return value;
  });

}

export function toSuperScript(str: string): string {
  const superMap = {
    "0": "⁰",
    "1": "¹",
    "2": "²",
    "3": "³",
    "4": "⁴",
    "5": "⁵",
    "6": "⁶",
    "7": "⁷",
    "8": "⁸",
    "9": "⁹",
    "+": "⁺",
    "−": "⁻",
    "-": "⁻",
    "=": "⁼",
    "(": "⁽",
    ")": "⁾",
    "A": "ᴬ",
    "a": "ᵃ",
    "Æ": "ᴭ",
    "ᴂ": "ᵆ",
    "ɐ": "ᵄ",
    "ɑ": "ᵅ",
    "ɒ": "ᶛ",
    "B": "ᴮ",
    "b": "ᵇ",
    "c": "ᶜ",
    "ɕ": "ᶝ",
    "D": "ᴰ",
    "d": "ᵈ",
    "ð": "ᶞ",
    "E": "ᴱ",
    "e": "ᵉ",
    "Ǝ": "ᴲ",
    "ə": "ᵊ",
    "ɛ": "ᵋ",
    "ɜ": "ᶟ",
    "f": "ᶠ",
    "G": "ᴳ",
    "g": "ᵍ",
    "ɡ": "ᶢ",
    "ɣ": "ˠ",
    "h": "ʰ",
    "H": "ᴴ",
    "ɦ": "ʱ",
    "I": "ᴵ",
    "i": "ⁱ",
    "ɪ": "ᶦ",
    "ɨ": "ᶤ",
    "ᵻ": "ᶧ",
    "ɩ": "ᶥ",
    "j": "ʲ",
    "J": "ᴶ",
    "ʝ": "ᶨ",
    "ɟ": "ᶡ",
    "K": "ᴷ",
    "k": "ᵏ",
    "l": "ˡ",
    "L": "ᴸ",
    "ʟ": "ᶫ",
    "ᶅ": "ᶪ",
    "ɭ": "ᶩ",
    "M": "ᴹ",
    "m": "ᵐ",
    "ɱ": "ᶬ",
    "N": "ᴺ",
    "n": "ⁿ",
    "ɴ": "ᶰ",
    "ɲ": "ᶮ",
    "ɳ": "ᶯ",
    "ŋ": "ᵑ",
    "O": "ᴼ",
    "o": "ᵒ",
    "ɔ": "ᵓ",
    "ᴖ": "ᵔ",
    "ᴗ": "ᵕ",
    "ɵ": "ᶱ",
    "Ȣ": "ᴽ",
    "P": "ᴾ",
    "p": "ᵖ",
    "ɸ": "ᶲ",
    "r": "ʳ",
    "R": "ᴿ",
    "ɹ": "ʴ",
    "ɻ": "ʵ",
    "ʁ": "ʶ",
    "s": "ˢ",
    "ʂ": "ᶳ",
    "ʃ": "ᶴ",
    "T": "ᵀ",
    "t": "ᵗ",
    "ƫ": "ᶵ",
    "U": "ᵁ",
    "u": "ᵘ",
    "ᴜ": "ᶸ",
    "ᴝ": "ᵙ",
    "ʉ": "ᶶ",
    "ɥ": "ᶣ",
    "ɯ": "ᵚ",
    "ɰ": "ᶭ",
    "ʊ": "ᶷ",
    "v": "ᵛ",
    "V": "ⱽ",
    "ʋ": "ᶹ",
    "ʌ": "ᶺ",
    "w": "ʷ",
    "W": "ᵂ",
    "x": "ˣ",
    "y": "ʸ",
    "z": "ᶻ",
    "ʐ": "ᶼ",
    "ʑ": "ᶽ",
    "ʒ": "ᶾ",
    "ꝯ": "ꝰ",
    "ᴥ": "ᵜ",
    "β": "ᵝ",
    "γ": "ᵞ",
    "δ": "ᵟ",
    "θ": "ᶿ",
    "φ": "ᵠ",
    "χ": "ᵡ",
    "н": "ᵸ",
    "ნ": "ჼ",
    "ʕ": "ˤ",
    "ⵡ": "ⵯ"
  };

  return str.replace(/(.)/g, function (match) {
    if(!superMap[match])
      console.log(match);
    return superMap[match];
  });
}

export function patternSearch(pattern: string, values: (string | number)[]): boolean {
  let patternToUse = (pattern || '').toLocaleLowerCase();

  const stringValues = values.map(value => {
    return (value || '').toString().toLocaleLowerCase();
  });

  if (patternToUse.indexOf('*') !== -1) {
    const fromStart = patternToUse.startsWith('*');
    const fromEnd = patternToUse.endsWith('*');
    if (fromStart) {
      const toSearch = patternToUse.replace('*', '');
      return stringValues.filter(value => value.endsWith(toSearch)).length > 0;
    }
    if (fromEnd) {
      const toSearch = patternToUse.replace('*', '');
      return stringValues.filter(value => value.startsWith(toSearch)).length > 0;
    }
  }
  return stringValues.filter(value => {
    return value.indexOf(patternToUse) !== -1;
  }).length > 0;
}

export function getBase64(file: File): Observable<string> {
  const reader = new FileReader();
  reader.readAsDataURL(file);

  return new Observable((observer: Observer<string>) => {
    reader.onload = () => {
      const base64 = (reader.result as string).replace(/^data\:(\S*);base64\,/, '');
      observer.next(base64);
      observer.complete();
    };
    reader.onerror = (error) => {
      observer.error(error);
    }
  });
}



